import {useMaterials} from '@domain/materials'
import {CONFIG_TYPE} from '@hconnect/common/consts'
import {UpmEntityType, TreeNode, EntityConfigDto} from '@hconnect/common/types'
import {Loader} from '@hconnect/uikit/src/lib2'
import {useMemo} from 'react'

import {useUrlParam} from '../../../routing'
import {useCreateEntityConfig} from '../../hooks/upmEntityConfig/useCreateEntityConfig'
import {useDepartmentDetails} from '../../hooks/upmEntityConfig/useDepartmentDetails'
import {useEntityConfig} from '../../hooks/upmEntityConfig/useEntityConfig'
import {useUpdateEntityConfig} from '../../hooks/upmEntityConfig/useUpdateEntityConfig'
import {ItemDetailsLayout} from '../../plant-setup-item-layout'
import {useSelectedItem} from '../../providers'

import {ConfigForm} from './ConfigForm'
import {PlantConfigForm} from './PlantConfigForm'

const departmentHeaders = {
  '05_5': 'WHR',
  '05_6': 'COPG',
  '06_1': 'FuelMaterialsHandling',
  '06_4': 'CoalMill',
  '10_2': 'Quarry',
  '20_4': 'Crusher',
  '25_1': 'RawMaterialDryer',
  '25_2': 'RawMaterialDeforesting',
  '30_4': 'RawMill',
  '40_4': 'Kiln',
  '41_1': 'ClayCalcination',
  '45_0': 'CementDryer',
  '45_1': 'CementDryer',
  '45_2': 'SemiFinishedMill',
  '49_0': 'CarbonCaptureUtilization',
  '50_4': 'CementMill',
  '55_1': 'CementBlending',
  Plant: 'Plant'
}

const getInitialConfig = (
  plantUpmId: string,
  upmType: UpmEntityType,
  departmentConfig?: EntityConfigDto,
  upmId?: string
): EntityConfigDto => {
  return {
    metadata: {
      configurationType: CONFIG_TYPE,
      plantId: plantUpmId,
      typeUpmId: upmId ?? '',
      upmType
    },
    payload: {productionData: departmentConfig?.payload?.productionData || []}
  }
}

interface ConfigurationProps {
  selectedItem: TreeNode
}

export const Configuration: React.FC<ConfigurationProps> = ({selectedItem}) => {
  const entityCodesPath = useMemo(() => {
    return selectedItem.entityCodesPath ? selectedItem.entityCodesPath : ''
  }, [selectedItem.entityCodesPath])

  const isDepartment = selectedItem.itemType === UpmEntityType.Department

  const {upmPlantId} = useSelectedItem()
  const {
    data: entityConfig,
    isLoading,
    isFetching
  } = useEntityConfig(upmPlantId, selectedItem.upmId, selectedItem.itemType)
  const {data: departmentDetails, isLoading: isLoadingDepartment} = useDepartmentDetails(
    isDepartment ? selectedItem.upmId : undefined
  )
  const {mutateAsync: createEntityConfig} = useCreateEntityConfig()
  const {mutateAsync: updateEntityConfig} = useUpdateEntityConfig()

  const plantCode = useUrlParam('plantCode')
  const {
    data: materials,
    isLoading: isLoadingMaterials,
    isFetching: isFetchingMaterials
  } = useMaterials({plantCode})
  const configNotCreated = useMemo(() => !isLoading && !entityConfig, [entityConfig, isLoading])
  const isLoadingOrFetching =
    isLoading ||
    isFetching ||
    isLoadingMaterials ||
    isFetchingMaterials ||
    (isDepartment && isLoadingDepartment)

  if (isLoadingOrFetching) {
    return (
      <ItemDetailsLayout>
        <Loader />
      </ItemDetailsLayout>
    )
  }

  const initialConfig: EntityConfigDto = getInitialConfig(
    upmPlantId,
    selectedItem.itemType,
    entityConfig,
    selectedItem.upmId
  )

  const config = entityConfig || initialConfig

  const handleCreateConfig = (entityConfig: EntityConfigDto) =>
    createEntityConfig({plantUpmId: upmPlantId, entityConfig, type: selectedItem.itemType})

  const handleUpdateConfig = (entityConfig: EntityConfigDto) =>
    updateEntityConfig({plantUpmId: upmPlantId, entityConfig, type: selectedItem.itemType})

  const onSubmit = configNotCreated ? handleCreateConfig : handleUpdateConfig

  const type = selectedItem.itemType === UpmEntityType.Plant ? 'Plant' : entityCodesPath

  if (selectedItem.itemType === UpmEntityType.Plant) {
    return (
      <PlantConfigForm
        selectedItem={selectedItem}
        configNotCreated={configNotCreated}
        config={config}
        onSubmit={onSubmit}
        materials={materials || []}
      />
    )
  }

  return (
    <ConfigForm
      selectedItem={selectedItem}
      configNotCreated={configNotCreated}
      config={config}
      onSubmit={onSubmit}
      materials={materials || []}
      type={departmentHeaders[type]}
      departmentDetails={departmentDetails}
    />
  )
}
